/**
 * PR-Audit-02B G-02 (2026-06-19) — Room editor modal.
 *
 * Reuses the dialog chrome pattern from StaffUsersPage's Create User
 * modal (`fixed inset-0 z-50 flex items-center justify-center
 * bg-black/70 p-4`). Single source of truth: existing dark-theme
 * Tailwind tokens, existing form patterns, existing API client.
 *
 * Modes:
 *   * mode="create" → POST /api/owner/brands/{brand_id}/branches/{branch_id}/rooms
 *   * mode="edit"   → PATCH /api/owner/brands/{brand_id}/rooms/{room_id}
 *   * mode="delete" → DELETE /api/owner/brands/{brand_id}/rooms/{room_id} (with confirm)
 *
 * Tier-1 invariants preserved:
 *   - No new component library introduced
 *   - No fork of existing components
 *   - No new endpoints — uses existing `api.ownerCreateRoom` /
 *     `ownerUpdateRoom` / `ownerDeleteRoom` (added in lib/api.js)
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const ROOM_CATEGORIES = ['VIP', 'Main Hall', 'Outdoor', 'Lounge', 'Private'];

export default function RoomEditorModal({
  open,
  mode = 'create',         // 'create' | 'edit' | 'delete'
  branchId,
  room,                    // required for edit/delete
  onClose,
  onSuccess,
}) {
  const { token, user } = useAuthStore();
  const brandId = user?.brand_id;
  const qc = useQueryClient();

  const [form, setForm] = useState({
    name: '',
    category: 'VIP',
    capacity: 8,
    description: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && room) {
      setForm({
        name: room.name || '',
        category: room.category || 'VIP',
        capacity: room.capacity || 8,
        description: room.description || '',
      });
    } else {
      setForm({ name: '', category: 'VIP', capacity: 8, description: '' });
    }
    setError('');
  }, [open, mode, room]);

  const invalidateAfter = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: qk.ownerFloorPlanState(branchId) }),
      qc.invalidateQueries({ queryKey: ['branch-rooms', branchId] }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.ownerCreateRoom(token, brandId, branchId, {
        name: form.name.trim(),
        category: form.category.trim(),
        capacity: Number(form.capacity),
        description: form.description.trim() || null,
      }),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.ownerUpdateRoom(token, brandId, room.id, {
        name: form.name.trim(),
        category: form.category.trim(),
        capacity: Number(form.capacity),
        description: form.description.trim() || null,
      }),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.ownerDeleteRoom(token, brandId, room.id),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Delete failed'),
  });

  if (!open) return null;
  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const title =
    mode === 'create' ? 'Add room'
    : mode === 'edit' ? `Edit room: ${room?.name || ''}`
    : `Delete room: ${room?.name || ''}`;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setError('');
    if (mode === 'create') return createMutation.mutate();
    if (mode === 'edit') return updateMutation.mutate();
    if (mode === 'delete') return deleteMutation.mutate();
  };

  return (
    <div
      data-testid="room-editor-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-[#15151B] border border-white/10 p-6 space-y-4"
        data-testid={`room-editor-${mode}`}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="room-editor-close"
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white/70 hover:bg-white/5 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {mode === 'delete' ? (
          <p className="text-sm text-white/70" data-testid="room-editor-delete-confirm">
            This will deactivate <span className="text-gold">{room?.name}</span> and its tables.
            Reservations referencing this room remain intact.
          </p>
        ) : (
          <>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                maxLength={80}
                data-testid="room-editor-name"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/60"
                placeholder="e.g. VIP Room A"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="room-editor-category"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white"
              >
                {ROOM_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Capacity</label>
              <input
                type="number"
                min={1}
                max={200}
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                required
                data-testid="room-editor-capacity"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Description (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                data-testid="room-editor-description"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white"
              />
            </div>
          </>
        )}

        {error ? (
          <div
            data-testid="room-editor-error"
            className="text-sm text-red-300 bg-red-900/20 border border-red-700/30 rounded-2xl px-3 py-2"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="room-editor-cancel"
            className="px-4 py-2 rounded-2xl text-sm text-white/80 hover:bg-white/5 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="room-editor-submit"
            className={
              'px-4 py-2 rounded-2xl text-sm font-semibold min-h-[44px] ' +
              (mode === 'delete'
                ? 'bg-red-600/20 text-red-200 border border-red-600/40 hover:bg-red-600/30'
                : 'bg-gold/20 text-gold border border-gold/40 hover:bg-gold/30')
            }
          >
            {busy ? 'Working…'
              : mode === 'create' ? 'Create room'
              : mode === 'edit' ? 'Save changes'
              : 'Confirm delete'}
          </button>
        </div>
      </form>
    </div>
  );
}
